import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';

import Button from 'components/partials/button/button';
import Heading from 'components/partials/heading/heading';
import TableComponent from 'components/partials/table-component/table-component';
import Loader from 'components/partials/loader/loader';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import {
  getProductsTableSettings,
  type ProductRow,
} from 'containers/admin/products/products-table-settings';

import { useProductsList } from 'hooks/api/use-products';
import { useCurrentUser } from 'hooks/api/use-current-user';

import { useDictionaryByType } from 'hooks/use-dictionary-by-type';

import Icons from 'assets/icons';

import type { ProductTypeOption } from 'containers/admin/products/product/product-form';

interface ProductsAdminPageProps {
  clientId?: string | number;
}

const ProductsAdminPage = ({
  clientId,
}: ProductsAdminPageProps): React.JSX.Element => {
  const [productDictionary, setProductDictionary] = useState<
    ProductTypeOption[]
  >([]);

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);

  const currentUser = useCurrentUser();

  const productDictionaryQuery = useDictionaryByType(
    currentUser.data?.oktaUserId ?? '',
    'PRODUCT_TYPE'
  );

  const {
    data: productsResponse,
    isLoading,
    isError,
  } = useProductsList({
    pageSize: 1000,
    sortBy: sortingParams,
    filters: clientId ? { clientId } : null,
  });

  const products = (productsResponse?.data ?? []) as ProductRow[];

  const columns = getProductsTableSettings(productDictionary);

  useEffect(() => {
    const list = productDictionaryQuery.data;

    if (list) {
      setProductDictionary(
        list
          .filter((entry) => entry.name)
          .map((entry) => ({
            name: entry.name ?? '',
            description: entry.description ?? '',
          }))
      );
    }
  }, [productDictionaryQuery.data]);

  const renderContent = (): React.JSX.Element | null => {
    if ((!isError && !!products.length) || isLoading) {
      return (
        <TableComponent
          columns={columns}
          data={products}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          customHeight="max-h-[50vh]"
          pagination={false}
          isLoading={isLoading}
          manualSorting={false}
        />
      );
    }

    if (!isLoading && !products.length) {
      return (
        <EmptySpaceContent
          customClass="flex-1"
          text="There are no products to show yet."
        />
      );
    }

    return null;
  };

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      <>
        <div className="flex justify-between items-center my-5">
          <Loader loading={isLoading && !products.length} />
          <Heading variant="h1">MarketView Products</Heading>
          <RouterLink to="/admin/products/new">
            <Button
              variant="brand-filled"
              size="lg"
              iconBefore={<Icons.Plus aria-label="PlusIcon" />}
            >
              Add a Product
            </Button>
          </RouterLink>
        </div>

        <div className="relative flex flex-1 flex-col">{renderContent()}</div>
      </>
    </ResponsiveWrapper>
  );
};

export default ProductsAdminPage;
